import React, { useEffect, useRef, useState } from "react"
import ReactDatePicker from "react-datepicker"
import { Control } from "react-hook-form"
import { Input } from "reactstrap"
import { addYears, formatShortDateWithFormat, getStringLocaleDateFormat, valFuncRequired } from "swiipe.portal.shared"
import "./DateInput.scss"

interface IDateInput {
    name: string

    placeholder?: string
    errorMessage?: string
    required?: boolean

    formControl: Control

    valueDateFormat: string
    addFormatToPlaceholder?: boolean
    country?: string
    hiddenForCountries?: string[]
    minDate?: Date
    maxDate?: Date
    helpTypeSpecialChars?: boolean
}

export const DateInput = ({
    name,
    formControl,
    placeholder,
    errorMessage,
    required,
    country,
    valueDateFormat,
    hiddenForCountries,
    addFormatToPlaceholder,
    minDate,
    maxDate,
    helpTypeSpecialChars,
}: IDateInput) => {
    const visualDateFormat = helpTypeSpecialChars
        ? ensureFullDateFormat(getStringLocaleDateFormat())
        : getStringLocaleDateFormat()
    const hiddenDatePicker = country ? hiddenForCountries?.includes(country) : false
    const [dateSelected, setDateSelected] = useState<Date | undefined>(undefined)
    const lastLength = useRef<number>(99)

    const setDateValue = (date: Date) => {
        formControl.setValue(name, formatShortDateWithFormat(date, valueDateFormat))
        formControl.trigger()
    }

    useEffect(() => {
        formControl.trigger()
        if (!dateSelected) {
            return
        }
        setDateValue(dateSelected)
    }, [country])

    useEffect(() => {
        const value = formControl.getValues(name)
        if (value && new Date(value).getTime() !== dateSelected?.getTime()) {
            setDateSelected(new Date(value))
        }
    }, [formControl])

    return (
        <div id="date-input-container">
            <Input
                className="d-none"
                name={name}
                type="text"
                innerRef={
                    required
                        ? formControl.register(valFuncRequired(hiddenDatePicker ? "" : errorMessage ?? ""))
                        : formControl.register
                }
            />
            {!hiddenDatePicker && (
                <ReactDatePicker
                    placeholderText={
                        placeholder
                            ? placeholder + (addFormatToPlaceholder ? ` (${visualDateFormat.toUpperCase()})` : "")
                            : addFormatToPlaceholder
                            ? visualDateFormat.toUpperCase()
                            : ""
                    }
                    selected={dateSelected}
                    className="form-control"
                    onChange={(date) => {
                        if (date) {
                            setDateSelected(date)
                            setDateValue(date)
                        }
                    }}
                    onKeyDown={(e) => {
                        const target = e.currentTarget as HTMLInputElement
                        setTimeout(() => {
                            const newValue = target?.value ?? ""
                            if (newValue.length > lastLength.current && newValue.length < visualDateFormat.length) {
                                const nextChar = visualDateFormat[newValue.length]
                                const nextCharNeededTimes = countChar(nextChar, visualDateFormat)
                                const nextCharUsedTimes = countChar(nextChar, newValue)
                                if (
                                    !isNaN(parseInt(e.key)) &&
                                    nextChar &&
                                    !["d", "M", "y"].includes(nextChar) &&
                                    nextCharNeededTimes > nextCharUsedTimes
                                ) {
                                    target.value = newValue + nextChar
                                }
                            }
                            lastLength.current = newValue.length
                        })
                    }}
                    minDate={minDate ?? addYears(new Date(), -100)}
                    maxDate={maxDate ?? new Date()}
                    dateFormat={visualDateFormat}
                    showYearDropdown
                    showMonthDropdown
                />
            )}
        </div>
    )
}

function ensureFullDateFormat(format: string) {
    if (!format.includes("dd")) {
        format = format.replace("d", "dd")
    }
    if (!format.includes("MM")) {
        format = format.replace("M", "MM")
    }
    if (!format.includes("yyyy")) {
        if (format.includes("yyy")) {
            format = format.replace("yyy", "yyyy")
        } else if (format.includes("yy")) {
            format = format.replace("yy", "yyyy")
        } else if (format.includes("y")) {
            format = format.replace("y", "yyyy")
        }
    }
    return format
}

function countChar(char: string, str: string) {
    return str.split(char).length - 1
}
